import { useMemo } from 'react';

import { useFlags } from '@mint-lib/routing-context';
import { getRouteApi, useNavigate, type RouteApi } from '@mint/routing';

import { initialSearchParams } from '../constants/tablesConfig.js';

const useIsExternalData = () => {
  const ff = useFlags('@myn/secured', ['externalDataFeature']);
  return ff.externalDataFeature.enabled;
};

const CP_BASE = '/company-profile/import-hub';
const ED_BASE = '/external-data/import-hub';

export const getImportHubPaths = (isExternalData: boolean) => {
  const base = isExternalData ? ED_BASE : CP_BASE;
  return {
    performance: `${base}/performance/`,
    customMetrics: `${base}/custom-metrics`,
    whitelistedEmails: `${base}/whitelisted-emails`,
    importsNew: `${base}/performance/imports/new`,
    dataFile: `${base}/performance/imports/$dataFileId`,
    dataFileUpload: `${base}/performance/imports/$dataFileId/upload`,
    dataFileHeaderSelection: `${base}/performance/imports/$dataFileId/header-selection`,
    dataFileHeadersMapping: `${base}/performance/imports/$dataFileId/headers-mapping`,
    dataFileValidation: `${base}/performance/imports/$dataFileId/validation`,
    dataFileDataAssignment: `${base}/performance/imports/$dataFileId/data-assignment`,
  } as const;
};

export const useImportHubPaths = () => {
  const isExternalData = useIsExternalData();
  return useMemo(() => getImportHubPaths(isExternalData), [isExternalData]);
};

const cpImportHub = getRouteApi('/_secured/company-profile/import-hub');
const edImportHub = getRouteApi('/_secured/external-data/import-hub');

const cpPerformance = getRouteApi(
  '/_secured/company-profile/import-hub/performance/',
);
const edPerformance = getRouteApi(
  '/_secured/external-data/import-hub/performance/',
);

const cpDataFile = getRouteApi(
  '/_secured/company-profile/import-hub/performance/imports/$dataFileId',
);
const edDataFile = getRouteApi(
  '/_secured/external-data/import-hub/performance/imports/$dataFileId',
);

const cpDataAssignment = getRouteApi(
  '/_secured/company-profile/import-hub/performance/imports/$dataFileId/data-assignment',
);
const edDataAssignment = getRouteApi(
  '/_secured/external-data/import-hub/performance/imports/$dataFileId/data-assignment',
);

const cpValidation = getRouteApi(
  '/_secured/company-profile/import-hub/performance/imports/$dataFileId/validation',
);
const edValidation = getRouteApi(
  '/_secured/external-data/import-hub/performance/imports/$dataFileId/validation',
);

const cpWhitelistedEmails = getRouteApi(
  '/_secured/company-profile/import-hub/whitelisted-emails',
);
const edWhitelistedEmails = getRouteApi(
  '/_secured/external-data/import-hub/whitelisted-emails',
);

const cpCustomMetrics = getRouteApi(
  '/_secured/company-profile/import-hub/custom-metrics',
);
const edCustomMetrics = getRouteApi(
  '/_secured/external-data/import-hub/custom-metrics',
);

export const useImportHubLayoutRouteApi = ():
  | RouteApi<'/_secured/company-profile/import-hub'>
  | RouteApi<'/_secured/external-data/import-hub'> =>
  useIsExternalData() ? edImportHub : cpImportHub;

export const usePerformanceRouteApi = ():
  | RouteApi<'/_secured/company-profile/import-hub/performance/'>
  | RouteApi<'/_secured/external-data/import-hub/performance/'> =>
  useIsExternalData() ? edPerformance : cpPerformance;

export const useDataFileRouteApi = ():
  | RouteApi<'/_secured/company-profile/import-hub/performance/imports/$dataFileId'>
  | RouteApi<'/_secured/external-data/import-hub/performance/imports/$dataFileId'> =>
  useIsExternalData() ? edDataFile : cpDataFile;

export const useDataAssignmentRouteApi = ():
  | RouteApi<'/_secured/company-profile/import-hub/performance/imports/$dataFileId/data-assignment'>
  | RouteApi<'/_secured/external-data/import-hub/performance/imports/$dataFileId/data-assignment'> =>
  useIsExternalData() ? edDataAssignment : cpDataAssignment;

export const useValidationRouteApi = ():
  | RouteApi<'/_secured/company-profile/import-hub/performance/imports/$dataFileId/validation'>
  | RouteApi<'/_secured/external-data/import-hub/performance/imports/$dataFileId/validation'> =>
  useIsExternalData() ? edValidation : cpValidation;

export const useWhitelistedEmailsRouteApi = ():
  | RouteApi<'/_secured/company-profile/import-hub/whitelisted-emails'>
  | RouteApi<'/_secured/external-data/import-hub/whitelisted-emails'> =>
  useIsExternalData() ? edWhitelistedEmails : cpWhitelistedEmails;

export const useCustomMetricsRouteApi = ():
  | RouteApi<'/_secured/company-profile/import-hub/custom-metrics'>
  | RouteApi<'/_secured/external-data/import-hub/custom-metrics'> =>
  useIsExternalData() ? edCustomMetrics : cpCustomMetrics;

export const useImportHubPaginatedSearch = () => {
  const search = useImportHubLayoutRouteApi().useSearch();
  return { ...initialSearchParams, ...search };
};

export const useImportHubNavigate = () => useNavigate();
